import {css, StyleSheet} from 'aphrodite';
import $ from 'jquery';
import React, {useRef, useState} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {renderToDOM} from 'redux-stores/ReduxDOMRender';
import * as Colors from 'ui-library/Colors';
import {warn} from 'util/Logging';
import {isServerRequestError} from 'util/Net';
import {getJSONPromise} from 'util/Net';

export function initializeZendesk(): void {
  const $zendeskMessengerNode = $('.zendeskMessenger-mount');
  // If the mount isn't on the page (e.g. outside the app), don't load the chat.
  if (!$zendeskMessengerNode.length) {
    return;
  }
  renderToDOM(<ZendeskMessenger />, $zendeskMessengerNode[0]);
}

enum EventTypes {
  METADATA = 'zendesk-metadata',
  RENEW_JWT_RESPONSE = 'zendesk-renew-jwt-response',
  RENEW_JWT_REQUEST = 'zendesk-renew-jwt-request',
  OPEN_MESSENGER = 'open-zendesk-messenger',
  CLOSE_MESSENGER = 'close-zendesk-messenger',
  REFERRAL_URL_REQUEST = 'referral-url-request',
  REFERRAL_URL_RESPONSE = 'referral-url-response',
}

function ZendeskMessenger(): JSX.Element {
  const iframeNodeRef = useRef<HTMLIFrameElement | null>(null);
  const [isOpen, updateIsOpen] = useState<boolean>(false);

  if (!BenchlingConfig.CDN_DOMAIN) {
    warn('ZendeskMessenger cannot be initialized without CDN_DOMAIN.', {
      extra: {
        CDN_DOMAIN: BenchlingConfig.CDN_DOMAIN,
        ENABLE_ZENDESK_MESSAGING: BenchlingConfig.ENABLE_ZENDESK_MESSAGING,
        ENABLE_IN_APP_SUPPORT: BenchlingConfig.ENABLE_IN_APP_SUPPORT,
      },
    });
    return <></>;
  }

  const targetOrigin = `https://${BenchlingConfig.CDN_DOMAIN}`;

  interface UserMetadata {
    tenantSubdomain: string;
    tenantGlobalIdentifier: string;
    email: string;
    name: string;
    iat: bigint;
    exp: bigint;
    hs256Signature: string;
  }

  interface CustomHelpOption {
    label: string;
    url: string;
  }

  interface AuthZendeskResponse {
    integrationId: string;
    jwt: string;
    globalUserIdentifier: string;
    userMetadata: UserMetadata;
    customHelpOption: CustomHelpOption;
  }

  const authenticateMessenger = async (isRenew: boolean = false): Promise<void> => {
    try {
      const response: AuthZendeskResponse = await getJSONPromise('/1/api/zendesk-sunco-jwt-sso', {
        headers: {'Content-Type': 'application/json'},
      });
      if (!iframeNodeRef || !iframeNodeRef.current || !iframeNodeRef.current.contentWindow) {
        return;
      }
      response.customHelpOption = BenchlingConfig.SUPPORT_CUSTOM_HELP_OPTION;
      const message = {type: isRenew ? EventTypes.RENEW_JWT_RESPONSE : EventTypes.METADATA, data: response};
      iframeNodeRef.current.contentWindow.postMessage(message, targetOrigin);
    } catch (err) {
      // If we fail to authenticate, don't show the messenger.
      if (!isServerRequestError(err)) {
        throw err;
      }
    }
  };

  function addZendeskEventListener(): void {
    window.addEventListener(
      'message',
      (e) => {
        if (e.origin !== targetOrigin) {
          return;
        }
        if (e.data.type === EventTypes.RENEW_JWT_REQUEST) {
          authenticateMessenger(true);
        } else if (e.data.type === EventTypes.OPEN_MESSENGER) {
          updateIsOpen(true);
        } else if (e.data.type === EventTypes.CLOSE_MESSENGER) {
          updateIsOpen(false);
        } else if (e.data.type === EventTypes.REFERRAL_URL_REQUEST) {
          if (!iframeNodeRef || !iframeNodeRef.current || !iframeNodeRef.current.contentWindow) {
            return;
          }
          const message = {type: EventTypes.REFERRAL_URL_RESPONSE, referralUrl: window.location.pathname};
          iframeNodeRef.current.contentWindow.postMessage(message, targetOrigin);
        }
      },
      false
    );
  }

  function onLoad(): void {
    addZendeskEventListener();
    authenticateMessenger();
  }

  return (
    <iframe
      ref={iframeNodeRef}
      id="ze-iframe"
      title="zendesk"
      className={css(styles.messengerWindow, isOpen && styles.open, !isOpen && styles.closed)}
      onLoad={onLoad}
      src={`${targetOrigin}/static/cdn-only/zendesk/index.html?cacheKey=f440d839173fe3b436664b2ea60db56c`}
      sandbox="allow-same-origin allow-scripts allow-popups"
    />
  );
}

const styles = StyleSheet.create({
  messengerWindow: {
    position: 'absolute',
    border: 'none',
    zIndex: 1060,
    'border-radius': '25px',
  },
  open: {width: '374px', height: '492px', boxShadow: `-1px 1px 3px ${Colors.GRAY_DARK}`},
  closed: {width: '54px', height: '60px', boxShadow: 'none'},
});

export default ZendeskMessenger;
